import { Box, Container, Typography, useTheme } from "@mui/material";
import ExecutionCard from "./ExecutionCard";
import { useTranslate } from "../../hooks/useTranslate";
import { setActiveExecution } from "../../stores/statusStore/statusStore";
import { useCallback, useEffect } from "react";
import { YantraExecution } from "../../tanstack/queries/player";
import { useLocation, useNavigate } from "react-router";
import { setLastBet } from "../../stores/cardsStore/CardsStore";
import firePostMessage from "../../utils/firePostMessage";
import HistoryBtn from "../history/HistoryBtn";

interface IExecutionsListProps {
  executions: YantraExecution[];
}

export default function ExecutionsList({ executions }: IExecutionsListProps) {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const theme = useTheme();

  const handleExecutionSelect = useCallback(
    (execution: YantraExecution) => {
      setLastBet({ images: {}, rows: {} });
      navigate({ pathname: `/${execution.id}`, search });

      firePostMessage({ type: "round_change", id: execution.id });
    },
    [navigate, search]
  );

  useEffect(() => {
    if (executions.length === 1) {
      const firstExecution = executions[0];
      if (firstExecution) {
        setActiveExecution(firstExecution.id, firstExecution.yantraId);
      }
    }
  }, [executions]);

  return (
    <Container
      maxWidth="lg"
      disableGutters
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      {pathname !== "/error" && (
        <Box
          sx={{
            px: 1,
            py: 0.5,
            borderBottomRightRadius: { xs: 0, sm: theme.spacing(4) },
            borderTopRightRadius: { xs: 0, sm: theme.spacing(4) },
            borderBottomLeftRadius: { xs: theme.spacing(4), sm: 0 },
            borderTopLeftRadius: { xs: theme.spacing(4), sm: 0 },
            bgcolor: "rgba(30, 30, 30, 0.7)",
            position: "fixed",
            zIndex: 15,
            top: { xs: theme.spacing(2), sm: theme.spacing(10) },
            left: { xs: "auto", sm: 0 },
            right: { xs: 0, sm: "auto" },
          }}
        >
          <HistoryBtn />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          height: "100%",
          flexDirection: { xs: "column", lg: "row" },
          pt: 2.5,
        }}
      >
        {!executions.length ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              gap: 1,
              alignItems: "center",
              alignSelf: "start",
              width: "100%",
              background: "rgb(255 78 76)",
              borderRadius: 3,
              p: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: (theme) => theme.typography.pxToRem(48),
                color: "white",
              }}
            >
              {t("main.noGames")}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: (theme) => theme.spacing(2),
              justifyContent: "center",
            }}
          >
            {executions
              .sort((a, b) => a.payout - b.payout)
              .map((execution) => (
                <ExecutionCard onSelectExecution={() => handleExecutionSelect(execution)} execution={execution} key={execution.id} />
              ))}
          </Box>
        )}
      </Box>
    </Container>
  );
}
